/* ----------------------------------------------------------------------------
 * Voice state machine — a pure reducer (no DOM, no timers, no I/O).
 *
 * Interaction model: PUSH-TO-TALK (hold to record, release to send).
 * States: idle -> listening -> thinking -> speaking -> error, with press/hold,
 * release, stop, mute, interrupt/barge-in and a text-fallback path
 * (research doc §2). `finalizing` marks the brief window between RELEASE and the
 * transcript arriving (STT POST in flight) so the UI can say "Sending…".
 *
 * SAFETY INVARIANT (locked): the machine NEVER performs an external action. A
 * ProposedAction is parked in `pendingApproval`; only an explicit APPROVE event
 * clears it into `approvedAction`, which the caller then executes. There is no
 * transition that auto-approves. Proven by the approval test.
 * -------------------------------------------------------------------------- */

import type { VoiceStatus, VoiceError, ProposedAction } from '../types.ts';
import type { NarrationSegment } from '../briefing/contract.ts';

export interface VoiceState {
  status: VoiceStatus;
  muted: boolean;
  /** hands-free "Hey Aura" armed indicator (ADDITIVE, informational only:
   *  set SOLELY by WAKE_ARMED / WAKE_DISARMED and read by the wake-armed visual.
   *  It NEVER affects status, any transition, or the SAFETY INVARIANT). */
  handsFreeArmed: boolean;
  /** true while the text-input fallback is the active input modality. */
  textFallback: boolean;
  /** true between RELEASE and the transcript landing (STT POST in flight). */
  finalizing: boolean;
  /** true ONLY once audio capture is genuinely LIVE (mic open + STT stream
   *  recording). The controller sets this via PRESS_START, which it dispatches
   *  EXCLUSIVELY after getUserMedia + the STT stream have actually opened, so it
   *  gates the finalizing/"Sending…" transition: Sending can NEVER begin before
   *  audio is captured (first-permission race fix, 2026-07-22). */
  captureStarted: boolean;
  interimTranscript: string;
  finalTranscript: string;
  /** the last utterance handed to the brain (thinking input). */
  committedUtterance: string;
  error: VoiceError | null;
  /** set when barge-in fires so the caller cancels in-flight TTS. */
  cancelTts: boolean;
  /** parked proposal awaiting explicit user confirm — NEVER auto-run. */
  pendingApproval: ProposedAction | null;
  /** set ONLY by APPROVE; the caller reads it, executes, then RESET_ACTION. */
  approvedAction: ProposedAction | null;
}

export type VoiceEvent =
  // Push-to-talk: PRESS_START on hold-begin, PRESS_END on release.
  | { type: 'PRESS_START' }
  | { type: 'PRESS_END' }
  | { type: 'STT_INTERIM'; text: string }
  | { type: 'STT_FINAL'; text: string }
  | { type: 'SUBMIT_TEXT'; text: string }
  | { type: 'THINKING_STARTED' }
  | { type: 'SPEAK' }
  | { type: 'SPEAK_FAILED' }
  | { type: 'SPEAKING_ENDED' }
  | { type: 'STOP' }
  | { type: 'MUTE' }
  | { type: 'UNMUTE' }
  | { type: 'INTERRUPT' }
  | { type: 'ERROR'; error: VoiceError }
  | { type: 'RESET' }
  | { type: 'ENABLE_TEXT_FALLBACK' }
  | { type: 'DISABLE_TEXT_FALLBACK' }
  | { type: 'REQUEST_APPROVAL'; action: ProposedAction }
  | { type: 'APPROVE' }
  | { type: 'REJECT' }
  | { type: 'CLEAR_APPROVED_ACTION' }
  | { type: 'WAKE_ARMED' }
  | { type: 'WAKE_DISARMED' };

export function initialVoiceState(): VoiceState {
  return {
    status: 'idle',
    muted: false,
    handsFreeArmed: false,
    textFallback: false,
    finalizing: false,
    captureStarted: false,
    interimTranscript: '',
    finalTranscript: '',
    committedUtterance: '',
    error: null,
    cancelTts: false,
    pendingApproval: null,
    approvedAction: null,
  };
}

/** Pure transition function. Given a state + event, returns the next state. */
export function voiceReducer(state: VoiceState, event: VoiceEvent): VoiceState {
  switch (event.type) {
    // ---- global events (valid from any status) ----------------------------
    case 'STOP':
      // Hard stop: back to idle, cancel any TTS, clear transient transcripts.
      return {
        ...state,
        status: 'idle',
        finalizing: false,
        captureStarted: false,
        interimTranscript: '',
        cancelTts: state.status === 'speaking',
        error: null,
      };

    case 'ERROR':
      return { ...state, status: 'error', finalizing: false, captureStarted: false, error: event.error, cancelTts: true };

    case 'RESET':
      return { ...initialVoiceState(), muted: state.muted, textFallback: state.textFallback, handsFreeArmed: state.handsFreeArmed };

    case 'MUTE':
      return { ...state, muted: true };

    case 'UNMUTE':
      return { ...state, muted: false };

    // ---- hands-free wake flag (ADDITIVE; touches ONLY handsFreeArmed) ------
    case 'WAKE_ARMED':
      return state.handsFreeArmed ? state : { ...state, handsFreeArmed: true };

    case 'WAKE_DISARMED':
      return state.handsFreeArmed ? { ...state, handsFreeArmed: false } : state;

    case 'ENABLE_TEXT_FALLBACK':
      return { ...state, textFallback: true };

    case 'DISABLE_TEXT_FALLBACK':
      return { ...state, textFallback: false };

    // ---- approval flow (never auto-executes) ------------------------------
    case 'REQUEST_APPROVAL':
      // Park the proposal. Status is unchanged; nothing external happens.
      return { ...state, pendingApproval: event.action };

    case 'APPROVE':
      // Only path that authorises an external action. Requires a pending one.
      if (!state.pendingApproval) return state;
      return { ...state, approvedAction: state.pendingApproval, pendingApproval: null };

    case 'REJECT':
      return { ...state, pendingApproval: null };

    case 'CLEAR_APPROVED_ACTION':
      return { ...state, approvedAction: null };

    // ---- input / listening (push-to-talk) ---------------------------------
    case 'PRESS_START':
      // Hold begins: start (or restart) listening from idle/speaking/error. If
      // currently speaking, this is a barge-in: cancel TTS first.
      return {
        ...state,
        status: 'listening',
        finalizing: false,
        // Capture is LIVE at this point — the controller dispatches PRESS_START
        // only after getUserMedia + the STT stream have actually opened.
        captureStarted: true,
        cancelTts: state.status === 'speaking',
        interimTranscript: '',
        finalTranscript: '',
        error: null,
      };

    case 'PRESS_END':
      // Release: stop recording and send. Stay 'listening' (so STT_FINAL is a
      // valid transition) but flag `finalizing` so the UI shows "Sending…".
      // A release from any non-listening status is a harmless no-op.
      // GUARD: finalizing/"Sending…" can NEVER begin before audio is actually
      // captured. A release that lands before capture is live (e.g. a mobile
      // permission prompt stealing the press gesture) is a harmless no-op.
      if (state.status !== 'listening' || !state.captureStarted) return state;
      return { ...state, finalizing: true, interimTranscript: '' };

    case 'INTERRUPT':
      // Barge-in during playback: stop speaking, reopen listening.
      if (state.status !== 'speaking') return state;
      // Re-open listening for the barge-in; capture is reopened by the controller
      // (startListening -> PRESS_START), so it is NOT yet live here.
      return { ...state, status: 'listening', finalizing: false, captureStarted: false, cancelTts: true, interimTranscript: '' };

    case 'STT_INTERIM':
      if (state.status !== 'listening') return state;
      return { ...state, interimTranscript: event.text };

    case 'STT_FINAL': {
      if (state.status !== 'listening') return state;
      const utterance = event.text.trim();
      if (utterance === '') {
        // Nothing intelligible — fall back to idle, no brain call.
        return { ...state, status: 'idle', finalizing: false, captureStarted: false, interimTranscript: '' };
      }
      return {
        ...state,
        status: 'thinking',
        finalizing: false,
        captureStarted: false,
        interimTranscript: '',
        finalTranscript: utterance,
        committedUtterance: utterance,
      };
    }

    case 'SUBMIT_TEXT': {
      // Text-fallback path: skip STT, go straight to thinking.
      const utterance = event.text.trim();
      if (utterance === '') return state;
      return {
        ...state,
        status: 'thinking',
        finalizing: false,
        captureStarted: false,
        finalTranscript: utterance,
        committedUtterance: utterance,
        interimTranscript: '',
      };
    }

    // ---- thinking / speaking ---------------------------------------------
    case 'THINKING_STARTED':
      // Idempotent guard: only meaningful while thinking.
      return state.status === 'thinking' ? { ...state, cancelTts: false } : state;

    case 'SPEAK':
      // Fires ONLY when audio actually starts (bound to real playback, never
      // optimistically). Valid from 'thinking' (normal turn) or 'idle' (a
      // tap-to-play replay of a reply the browser had blocked).
      if (state.status !== 'thinking' && state.status !== 'idle') return state;
      return { ...state, status: 'speaking', cancelTts: false };

    case 'SPEAK_FAILED':
      // Synthesis/playback did not produce audible sound (empty/muted/cancelled,
      // or autoplay blocked). Leave 'thinking' honestly for 'idle' instead of
      // showing 'speaking' in silence. If it was a block, needsPlaybackTap (in
      // the controller) surfaces a visible 'tap to play' control.
      if (state.status !== 'thinking') return state;
      return { ...state, status: 'idle', cancelTts: false };

    case 'SPEAKING_ENDED':
      if (state.status !== 'speaking') return state;
      return { ...state, status: 'idle', cancelTts: false };

    default: {
      // Exhaustiveness guard.
      const _never: never = event;
      return _never;
    }
  }
}

/**
 * The ONLY sanctioned predicate a caller may use before performing an external
 * side-effect: an action is authorised iff it sits in `approvedAction` (set
 * exclusively by APPROVE). A pending (un-approved) proposal is NOT authorised.
 */
export function isActionAuthorised(state: VoiceState): boolean {
  return state.approvedAction !== null;
}

/* ----------------------------------------------------------------------------
 * First-permission push-to-talk race (owner-reported, 2026-07-22).
 *
 * On a genuine FIRST use the browser shows a mic-permission prompt when the
 * controller calls getUserMedia(). On mobile that prompt STEALS the press
 * gesture: a pointerup/pointercancel fires WHILE the permission request is still
 * awaiting, so a "release" is queued BEFORE capture is live. Previously, once
 * permission was granted and capture opened, honouring that queued release
 * immediately dispatched PRESS_END -> finalizing ("Sending…") -> a phantom send
 * of empty audio (a duplicate STT request + an error), instead of Listening.
 *
 * This PURE decision tells the controller what a release that was deferred during
 * startup means, ONCE the mic + STT capture has resolved:
 *   - 'discard'        capture never came up (denied / unavailable / cancelled) —
 *                      nothing to send; the ERROR path drives the retry.
 *   - 'stay-listening' this press passed the FIRST permission prompt — the queued
 *                      release was the prompt stealing the gesture, NOT a real
 *                      hold-release. Approval => Listening; do NOT phantom-send.
 *                      (The listenGuard record-cap bounds the open capture.)
 *   - 'send'           permission was already granted (a subsequent, normal use):
 *                      a genuine quick hold-release -> send EXACTLY once. Desktop
 *                      and every non-first use are therefore UNCHANGED.
 * Pure + deterministic -> node --test.
 * -------------------------------------------------------------------------- */
export type DeferredReleaseDecision = 'send' | 'stay-listening' | 'discard';

export function resolveDeferredRelease(opts: {
  captureLive: boolean;
  firstPermissionGrant: boolean;
}): DeferredReleaseDecision {
  if (!opts.captureLive) return 'discard';
  return opts.firstPermissionGrant ? 'stay-listening' : 'send';
}

/* ----------------------------------------------------------------------------
 * Empty-transcript re-prompt (Lane E defect A, 2026-07-19). A hold-to-talk turn
 * that yields NO transcript (an ultra-short mobile capture that flushed no audio,
 * or genuine silence) must NOT be silently dropped. The controller surfaces this
 * general, phrase-agnostic re-prompt via the existing error channel so the user
 * is invited to try again instead of facing dead silence. Pure + node-testable.
 * -------------------------------------------------------------------------- */

/** The general re-prompt shown when a finalised transcript is empty/blank. It is
 *  phrase-agnostic: it depends ONLY on emptiness, never on the words spoken. */
export const EMPTY_TRANSCRIPT_REPROMPT: VoiceError = {
  code: "stt_failed",
  message: "Sorry, I didn't catch that — could you say that again?",
};

/** Minimum word count for a finalised transcript to count as a real request.
 *  Below this it is a FRAGMENT (a single letter like "A", a one-word remnant like
 *  "today"/"opening") that must NOT be sent to the brain or routed to any scripted
 *  content — the controller surfaces the ask-to-repeat re-prompt instead (owner
 *  directive 2026-07-22, revision). */
export const MIN_UTTERANCE_WORDS = 2;

/** Decide what a finalised STT transcript should trigger. "think" ONLY for a
 *  genuine multi-word utterance; otherwise "reprompt" (surface the ask-to-repeat).
 *  Reprompt when the transcript is empty / whitespace / punctuation-only, a single
 *  character, or a one-word fragment, OR when a reported STT confidence is below
 *  `minConfidence` (0 / undefined => not reported, never treated as low). Phrase-
 *  agnostic: depends ONLY on shape/quality, never on the words spoken — it is NOT a
 *  phrase allowlist. */
export function finalTranscriptAction(
  text: string,
  confidence?: number,
  minConfidence = 0.35,
): "think" | "reprompt" {
  const alnum = (text ?? "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  if (alnum === "") return "reprompt";
  if (alnum.split(/\s+/).length < MIN_UTTERANCE_WORDS) return "reprompt";
  if (typeof confidence === "number" && confidence > 0 && confidence < minConfidence) return "reprompt";
  return "think";
}


/* ----------------------------------------------------------------------------
 * Replay selectors (pure, additive — 2026-07-19). These do NOT change the
 * reducer, any event, or any transition: Replay reuses the EXISTING speak path
 * (SPEAK is already valid from 'idle', so a re-play naturally re-enters
 * 'speaking'). They live here because they are pure voice-state/data logic and
 * this is the module the Node --test suite can import (the controller pulls in
 * react + the '@/' alias and is not node-loadable).
 * -------------------------------------------------------------------------- */

/** The retained "last spoken response", replayed verbatim (no new AI call). */
export interface RetainedResponse {
  /** paced segments to re-speak through the existing SegmentedNarrator. */
  segments: NarrationSegment[];
  /** the display / transcript text of that response. */
  text: string;
}

/** Build the retained last-response from a spoken turn's segments (+ display
 *  text). Blank segments are dropped; a segment-less turn is retained as ONE
 *  flat block so Replay re-speaks it through the SAME paced path. Returns null
 *  when there is nothing speakable to replay. */
export function buildRetainedResponse(segments: NarrationSegment[], text: string): RetainedResponse | null {
  const clean = (Array.isArray(segments) ? segments : []).filter(
    (seg): seg is NarrationSegment => !!seg && typeof seg.text === 'string' && seg.text.trim() !== '',
  );
  const display = typeof text === 'string' ? text.trim() : '';
  if (clean.length > 0) {
    return { segments: clean, text: display !== '' ? display : clean.map((seg) => seg.text).join(' ') };
  }
  if (display !== '') return { segments: [{ text: display, pauseAfterMs: 0 }], text: display };
  return null;
}

/** Replay VISIBILITY: offered ONLY when a last response is retained AND Aura is
 *  idle/ready — never while listening / thinking / speaking / error. Mute does
 *  NOT hide it (a muted user may still replay; the click is simply silent). */
export function canReplayLast(status: VoiceStatus, retained: RetainedResponse | null): boolean {
  return retained !== null && status === 'idle';
}

/** Replay ACTION plan for a click: 'skip' when nothing to replay or not idle;
 *  'silent' when muted (keep the transcript on screen, make no sound —
 *  consistent with a muted reply); 'play' otherwise (re-speak through the
 *  existing cached-TTS path — no new /v1/ai call). */
export function replayPlan(
  status: VoiceStatus,
  muted: boolean,
  retained: RetainedResponse | null,
): 'play' | 'silent' | 'skip' {
  if (!canReplayLast(status, retained)) return 'skip';
  return muted ? 'silent' : 'play';
}
